import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useGameRoom } from '../hooks/useGameRoom';
import { useGameStore } from '../stores/gameStore';
import { CardHand } from '../components/game/PlayingCard';
import { DominoHand } from '../components/game/DominoTile';
import { SpadesTable } from '../components/game/SpadesTable';

export default function GamePage() {
  const { roomId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const gameType = searchParams.get('type') || 'spades';
  const vsAI = searchParams.get('vsAI') === 'true';

  const {
    isConnecting, error, phase, isMyTurn, setReady, leaveRoom,
    playCard, placeDomino, submitBid,
  } = useGameRoom(roomId, gameType, vsAI);

  const {
    players, mySessionId, currentTurnPlayerId, turnTimer,
    // Spades state
    team1Score, team1Bags, team2Score, team2Bags,
    team1Bid, team2Bid, team1Tricks, team2Tricks,
    currentTrick, bids, tricksWon, roundNumber, spadesBroken, validCards,
  } = useGameStore();

  const handleLeave = () => {
    leaveRoom();
    navigate('/lobby');
  };

  const isDominoes = gameType.startsWith('dominoes');
  const isSpades = gameType === 'spades';

  const playerList = Array.from(players.values());
  const myPlayer = players.get(mySessionId || '');

  const handleCardClick = (cardId: string) => {
    if (isMyTurn) {
      if (isDominoes) {
        placeDomino(cardId, 'left');
      } else {
        playCard(cardId);
      }
    }
  };

  if (isConnecting) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-shark-400 mx-auto mb-4"></div>
          <p className="text-xl">Connecting to game...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-xl mb-4">{error}</p>
          <button
            onClick={handleLeave}
            className="bg-shark-500 hover:bg-shark-600 text-white px-6 py-2 rounded-lg"
          >
            Back to Lobby
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold capitalize">{gameType.replace(/_/g, ' ')}</h1>
            <p className="text-gray-400">Room: {roomId}</p>
          </div>
          <div className="flex items-center space-x-4">
            {(phase === 'playing' || phase === 'bidding') && (
              <div className="text-center">
                <p className="text-sm text-gray-400">Timer</p>
                <p className="text-2xl font-bold text-shark-400">{turnTimer}s</p>
              </div>
            )}
            <button
              onClick={handleLeave}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
            >
              Leave
            </button>
          </div>
        </div>

        {/* Game Area */}
        <div className="bg-shark-800 rounded-xl p-6 min-h-[400px]">
          {phase === 'waiting' ? (
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-6">Waiting for Players</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {[0, 1, 2, 3].map((seatIndex) => {
                  const player = playerList.find((p) => p.seatIndex === seatIndex);
                  return (
                    <div
                      key={seatIndex}
                      className={`p-4 rounded-lg ${
                        player ? 'bg-shark-700' : 'bg-shark-900 border-2 border-dashed border-shark-600'
                      }`}
                    >
                      {player ? (
                        <>
                          <p className="font-semibold">{player.displayName}</p>
                          <p className="text-sm text-gray-400">
                            {player.isReady ? '✓ Ready' : 'Not Ready'}
                          </p>
                        </>
                      ) : (
                        <p className="text-gray-500">Waiting...</p>
                      )}
                    </div>
                  );
                })}
              </div>
              {myPlayer && !myPlayer.isReady && (
                <button
                  onClick={() => setReady(true)}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg text-lg font-semibold"
                >
                  Ready
                </button>
              )}
              {myPlayer?.isReady && (
                <button
                  onClick={() => setReady(false)}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-8 py-3 rounded-lg"
                >
                  Cancel Ready
                </button>
              )}
            </div>
          ) : isSpades ? (
            /* Spades game table */
            <SpadesTable
              players={playerList}
              mySessionId={mySessionId}
              currentTurnPlayerId={currentTurnPlayerId}
              phase={phase}
              currentTrick={currentTrick}
              bids={bids}
              tricksWon={tricksWon}
              team1Score={team1Score}
              team1Bags={team1Bags}
              team2Score={team2Score}
              team2Bags={team2Bags}
              team1Bid={team1Bid}
              team2Bid={team2Bid}
              team1Tricks={team1Tricks}
              team2Tricks={team2Tricks}
              roundNumber={roundNumber}
              spadesBroken={spadesBroken}
              onBidSubmit={submitBid}
            />
          ) : (
            /* Generic game area (Dominoes, Tonk, Bid Whist) */
            <div>
              <div className="text-center mb-6">
                <p className={`text-xl ${isMyTurn ? 'text-green-400 font-bold' : 'text-gray-400'}`}>
                  {isMyTurn ? "It's your turn! Click a tile to play." : `Waiting for other player...`}
                </p>
              </div>

              {/* Scores for non-Spades team games */}
              {gameType === 'bid_whist' && (
                <div className="flex justify-center space-x-8 mb-6">
                  <div className="bg-shark-700 rounded-lg px-6 py-3 text-center">
                    <p className="text-sm text-gray-400">Team 1</p>
                    <p className="text-2xl font-bold">{team1Score}</p>
                  </div>
                  <div className="bg-shark-700 rounded-lg px-6 py-3 text-center">
                    <p className="text-sm text-gray-400">Team 2</p>
                    <p className="text-2xl font-bold">{team2Score}</p>
                  </div>
                </div>
              )}

              {/* Other players */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                {playerList
                  .filter(p => p.sessionId !== mySessionId)
                  .map(player => (
                    <div key={player.sessionId} className="text-center">
                      <p className="text-sm text-gray-400 mb-2">{player.displayName}</p>
                      <p className="text-xs text-gray-500">{player.hand.length} tiles</p>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* My Hand */}
        {phase !== 'waiting' && myPlayer && myPlayer.hand.length > 0 && (
          <div className="mt-6 bg-shark-800 rounded-xl p-6">
            <h3 className="text-lg font-semibold mb-4">
              Your Hand ({myPlayer.hand.length} {isDominoes ? 'tiles' : 'cards'})
              {isSpades && validCards.length > 0 && isMyTurn && (
                <span className="text-sm text-green-400 ml-2">
                  ({validCards.length} playable)
                </span>
              )}
            </h3>
            {isDominoes ? (
              <DominoHand
                dominoes={myPlayer.hand}
                onDominoClick={handleCardClick}
                size="lg"
              />
            ) : (
              <CardHand
                cards={myPlayer.hand}
                onCardClick={handleCardClick}
                size="lg"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
